import { useMutation } from "@tanstack/react-query";
import { type IOverviewJointBillService } from "@services/overview/types";
import { queryClient } from "@/queryClient";
import {
  deleteProjectData,
  submitJointBillServiceDiscountCode,
  updateJointBillServiceEmailSubscriptionState,
  updateJointBillServicePaperBillDeliveryState,
  updateJointBillServiceProjectMailDeliverySettings,
  updateJointBillServiceSmsSubscription,
  updateProjectData,
} from "./services";
import type { IProjectData, ISubmitDiscountCodeRequest } from "./types";

export const useSubmitJointBillServiceDiscountCodeMutation = (data?: {
  onSuccess?: () => void;
}) =>
  useMutation<void, Error, ISubmitDiscountCodeRequest>({
    mutationFn: submitJointBillServiceDiscountCode,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSettings", variables.jointBillServiceId],
      });

      data?.onSuccess?.();
    },
  });

export const useSubmitJointBillServicePaperBillDeliveryMutation = (data?: {
  onSuccess?: () => void;
}) =>
  useMutation({
    mutationFn: updateJointBillServicePaperBillDeliveryState,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSettings", variables.jointBillServiceId],
      });

      data?.onSuccess?.();
    },
  });

export const useJointBillServiceEmailSubscriptionMutation = (data?: {
  onSuccess?: () => void;
}) =>
  useMutation({
    mutationFn: updateJointBillServiceEmailSubscriptionState,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSettings", variables.jointBillServiceId],
      });

      data?.onSuccess?.();
    },
  });

export const useJointBillServiceProjectMailDeliverySettingsMutation = (
  jointBillServiceId: number,
) =>
  useMutation({
    mutationFn: (data: { serviceProviderId: number; isChecked: boolean }) =>
      updateJointBillServiceProjectMailDeliverySettings({
        jointBillServiceId,
        ...data,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSettings", jointBillServiceId],
      });
    },
  });

export const useUpdateJointBillServiceSmsSubscriptionMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
) =>
  useMutation({
    mutationFn: (data: { isActive: boolean; phoneNumber: string }) =>
      updateJointBillServiceSmsSubscription(data, jointBillServiceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSettings", jointBillServiceId],
      });
    },
  });

export const useSubmitProjectDataMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
) =>
  useMutation({
    mutationFn: (data: IProjectData) =>
      updateProjectData(data, jointBillServiceId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["customer", "metadata"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["customer", "details"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSettings", jointBillServiceId],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServiceSettings",
            "projectData",
            jointBillServiceId,
          ],
        }),
      ]);
    },
  });

export const useDeleteProjectDataMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
) =>
  useMutation({
    mutationFn: () => deleteProjectData(jointBillServiceId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSettings", jointBillServiceId],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServiceSettings",
            "projectData",
            jointBillServiceId,
          ],
        }),
      ]);
    },
  });
